import BaseGameView from './engine/baseGameView';
import { ENGINES } from './gameFactory.types';
import { getLogger } from './logging/logger';

const logger = getLogger('GameFactory');

class GameFactory {
	constructor() {
		// Cache for previously looked up engines (Views)
		this.engineCache = {};

		// Cache for previously looked up engine cores (extends BaseView, Models)
		this.coreCache = {};
	}

	/**
	 * Mapping of the views which are most often used and the module needed to actually play
	 * a game in an arcade.
	 * @returns {Record<ENGINES, function>}
	 */
	get engines() {
		return {
			[ENGINES.advent]: () => import('./engine/advent/advent'),
			[ENGINES.ballotPicker]: () => import('./engine/ballotPicker/ballotPicker'),
			[ENGINES.bracket]: () => import('./engine/bracket/bracket'),
			[ENGINES.connectFour]: () => import('./engine/connectFour/connectFour'),
			[ENGINES.crossword]: () => import('./engine/crossword/crossword'),
			[ENGINES.dag]: () => import('./engine/dag/dag'),
			[ENGINES.documentViewer]: () => import('./engine/documentViewer/documentViewer'),
			[ENGINES.doubleExposure]: () => import('./engine/doubleExposure/doubleExposure'),
			[ENGINES.gamePool]: () => import('./engine/gamePool'),
			[ENGINES.home]: () => import('./engine/home/home'),
			[ENGINES.jigScene]: () => import('./engine/jigScene/jigScene'),
			[ENGINES.leaderboards]: () => import('./engine/leaderboards/leaderboards'),
			[ENGINES.lockBox]: () => import('./engine/lockBox/lockBox'),
			[ENGINES.loginGate]: () => import('./engine/registration/hwptvLoginGate'),
			[ENGINES.cognitoLoginGate]: () => import('./engine/registration/cognitoLoginGate'),
			[ENGINES.maze]: () => import('./engine/maze/maze'),
			[ENGINES.memoryMatch]: () => import('./engine/memoryMatch/memoryMatch'),
			[ENGINES.movieCubes]: () => import('./engine/movieCubes/movieCubesGL'),
			[ENGINES.guide]: () => import('./engine/guide/guide'),
			[ENGINES.path]: () => import('./engine/dag/dag'),
			[ENGINES.raffle]: () => import('./engine/raffle/raffle'),
			[ENGINES.cognitoRegistration]: () =>
				import('./engine/registration/cognitoRegistration'),
			[ENGINES.sceneStreak]: () => import('./engine/sceneStreak/sceneStreak'),
			[ENGINES.sectionedMenu]: () => import('./engine/sectionedMenu/sectionedMenu'),
			[ENGINES.slidePuzzle]: () => import('./engine/slidePuzzle/slidePuzzle'),
			[ENGINES.sportSchedule]: () => import('./engine/sportSchedule/sportSchedule'),
			[ENGINES.survey]: () => import('./engine/survey/survey'),
			[ENGINES.sweepstakes]: () => import('./engine/sweepstakes/sweepstakes'),
			[ENGINES.teamChooser]: () => import('./engine/teamChooser/teamChooser'),
			[ENGINES.teamStandings]: () => import('./engine/teamChooser/teamStandings'),
			[ENGINES.tilePop]: () => import('./engine/tilePop/tilePop'),
			[ENGINES.tileSwap]: () => import('./engine/tileSwap/tileSwap'),
			[ENGINES.trivia]: () => import('./engine/trivia/trivia'),
			[ENGINES.videoCarousel]: () => import('./engine/videoCarousel/videoCarousel'),
			[ENGINES.videoPage]: () => import('./engine/videoPage/videoPage'),
			[ENGINES.wellConnected]: () => import('./engine/wellConnected/wellConnected'),
			[ENGINES.wordSearch]: () => import('./engine/wordsearch/wordSearch'),
		};
	}

	/**
	 * Mapping of the cores for each engine which usually extend BaseGame.
	 */
	get games() {
		return {
			[ENGINES.advent]: () => import('./engine/advent/adventGame'),
			[ENGINES.ballotPicker]: () => import('./engine/ballotPicker/ballotPickerGame'),
			[ENGINES.bracket]: () => import('./engine/bracket/bracketGame'),
			[ENGINES.connectFour]: () => import('./engine/connectFour/connectFourGame'),
			[ENGINES.crossword]: () => import('./engine/crossword/crosswordGame'),
			[ENGINES.doubleExposure]: () => import('./engine/doubleExposure/doubleExposureGame'),
			[ENGINES.jigScene]: () => import('./engine/jigScene/jigSceneGame'),
			[ENGINES.lockBox]: () => import('./engine/lockBox/lockBoxGame'),
			[ENGINES.maze]: () => import('./engine/maze/mazeGame'),
			[ENGINES.memoryMatch]: () => import('./engine/memoryMatch/memoryMatchGame'),
			[ENGINES.movieCubes]: () => import('./engine/movieCubes/movieCubesGame'),
			[ENGINES.path]: () => import('./engine/dag/dagGame'),
			[ENGINES.dag]: () => import('./engine/dag/dagGame'),
			[ENGINES.sceneStreak]: () => import('./engine/sceneStreak/sceneStreakGame'),
			[ENGINES.slidePuzzle]: () => import('./engine/slidePuzzle/slidePuzzleGame'),
			[ENGINES.sportSchedule]: () => import('./engine/sportSchedule/sportScheduleGame'),
			[ENGINES.tilePop]: () => import('./engine/tilePop/tilePopGame'),
			[ENGINES.tileSwap]: () => import('./engine/tileSwap/tileSwapGame'),
			[ENGINES.trivia]: () => import('./engine/trivia/triviaGame'),
			[ENGINES.wellConnected]: () => import('./engine/wellConnected/wellConnectedGame'),
			[ENGINES.wordSearch]: () => import('./engine/wordsearch/wordSearchGame'),
		};
	}

	/**
	 * This method will spin up a new game instance with the provided data.
	 * @param {PageOptions} gameData Data to populate into a game
	 * @param {boolean} core Boolean value indicating it's the actua core (ie BaseGame extended)
	 * module to be used.
	 * @returns {Promise<BaseGameView<any>} Resolves an instantiated game.
	 */
	createGame(gameData, core = false) {
		logger.log('[createGame]', { gameData, core });

		if (this.supportsEngine(gameData.type, core)) {
			return this.getEngine(gameData.type, core).then((Engine) => new Engine(gameData));
		}

		return Promise.reject(new Error(`Unsupported engine type "${gameData.type}"`));
	}

	/**
	 * Returns just the engine class to be used for instantiating full versions of an engine with
	 * valid game data.
	 * @param {string} engineCode The engine code to return the engine class for
	 * @param {boolean} core Boolean indicating if the core (extends BaseGame) class should be
	 * returned instead of the regular View.
	 * @returns Returns the engine class that can be instantiated with valid game data.
	 */
	getEngine(engineCode, core = false) {
		logger.log('[getEngine]', { engineCode, core });

		if (!this.supportsEngine(engineCode)) {
			logger.error(`[Unsupported engine type][${engineCode}]`);
		}

		const cache = core ? this.coreCache : this.engineCache;
		if (cache[engineCode]) {
			return Promise.resolve(cache[engineCode]);
		}

		const source = core ? this.games : this.engines;
		return source[engineCode]().then((response) => {
			let module = response;

			if (module.default) {
				// Resolve actual instantiable module from default in ES6formatted module
				module = module.default;
			} else if (typeof module !== 'function') {
				// Resolve actual instantiable module from ES6 formatted module
				const moduleExportKeys = Object.keys(module).filter(
					(key) => !['DEFAULTS'].includes(key),
				);
				const likelyModuleName = moduleExportKeys.pop();
				module = module[likelyModuleName];
				if (moduleExportKeys.length) {
					logger.debug('[Module has Multiple exports. Is this the correct one?]');
					logger.debug({ likelyModuleName, moduleExportKeys });
				}
			} else {
				// No module resolution necessary
			}

			cache[module] = module;
			return module;
		});
	}

	/**
	 * Simple check of game data to ensure it looks like valid data that can be used for
	 * instantiating a game instance.
	 * @param {object} gameData The game data to be validated
	 * @returns Returns boolean value indicating if valid or not.
	 */
	isValidGameData(gameData) {
		logger.log('[isValidGameData]', { gameData });
		const { type, contentUrl } = gameData;
		return type && contentUrl;
	}

	/**
	 * Checks if the provided engine code is supported.
	 * @param {string} engineCode Engine code to check support for
	 * @param {boolean} core Boolean indicating if the BaseGame extended class should be checked.
	 * @returns Boolean value indicating if engine is supported.
	 */
	supportsEngine(engineCode, core = false) {
		logger.log('[supportsEngine]', { engineCode });

		const source = core ? this.games : this.engines;
		return !!source[engineCode];
	}
}

export default new GameFactory();
